import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, GripVertical, User, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface Professional {
  id: string;
  name: string;
  email: string;
  canPerformServices: boolean;
  priority?: number;
}

interface ProfessionalDisplayPriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  onUpdate?: () => void;
}

// Sortable Professional Item Component
const SortableProfessionalItem: React.FC<{ 
  professional: Professional; 
  index: number;
  totalItems: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
}> = ({ professional, index, totalItems, onMoveUp, onMoveDown }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: professional.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const canMoveUp = index > 0;
  const canMoveDown = index < totalItems - 1;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg transition-colors ${
        isDragging ? 'bg-blue-50 border-blue-300 shadow-lg' : 'hover:bg-gray-50 bg-white'
      }`}
    >
      <div className="flex items-center space-x-3 flex-1">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="w-5 h-5" />
        </div>
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#0B4778]/10 text-[#0B4778] font-semibold text-sm">
          <User className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-gray-900">{professional.name}</h3>
          {/* Email - hidden on mobile */}
          <p className="hidden md:block text-sm text-gray-500">{professional.email}</p>
          {/* Priority - shown on mobile instead of email */}
          <p className="md:hidden text-sm text-gray-500">Priority: {index + 1}</p>
        </div>
        {/* Priority column - hidden on mobile */}
        <div className="hidden md:block text-sm text-gray-500 mr-2">
          Priority: {index + 1}
        </div>
      </div>
      
      {/* Up/Down Arrow Buttons */}
      <div className="flex flex-col gap-1 ml-2">
        <button
          onClick={onMoveUp}
          disabled={!canMoveUp}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Move up"
          aria-label="Move up"
        >
          <ArrowUp className="w-4 h-4 text-gray-700" />
        </button>
        <button
          onClick={onMoveDown}
          disabled={!canMoveDown}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Move down"
          aria-label="Move down"
        >
          <ArrowDown className="w-4 h-4 text-gray-700" />
        </button>
      </div>
    </div>
  );
};

const ProfessionalDisplayPriorityModal: React.FC<ProfessionalDisplayPriorityModalProps> = ({
  isOpen,
  onClose,
  businessId,
  onUpdate,
}) => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load professionals with priorities
  useEffect(() => {
    if (isOpen && businessId) {
      loadProfessionals();
    }
  }, [isOpen, businessId]);

  const loadProfessionals = async () => {
    setLoading(true);
    try {
      const data = await apiService.getBusinessProfessionalsWithPriorities(businessId);
      // Sort by priority (lower number = higher priority)
      const sorted = data.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
      setProfessionals(sorted);
    } catch (error) {
      console.error('Error loading professionals:', error);
      toast.error('Failed to load professionals');
    } finally {
      setLoading(false);
    }
  };

  const reorderProfessionals = (oldIndex: number, newIndex: number) => {
    // Create reordered array
    const reorderedProfessionals = arrayMove(professionals, oldIndex, newIndex);

    // Update priority values (index + 1, where 1 is highest priority)
    const updatedProfessionals = reorderedProfessionals.map((professional, index) => ({
      ...professional,
      priority: index + 1
    }));

    // Update local state immediately for better UX
    setProfessionals(updatedProfessionals);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = professionals.findIndex(prof => prof.id === active.id);
      const newIndex = professionals.findIndex(prof => prof.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderProfessionals(oldIndex, newIndex);
      }
    }
  };

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      reorderProfessionals(index, index - 1);
    }
  };

  const handleMoveDown = (index: number) => {
    if (index < professionals.length - 1) {
      reorderProfessionals(index, index + 1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Create priority updates array
      const priorityUpdates = professionals.map((prof, index) => ({
        professionalId: prof.id,
        priority: index + 1
      }));

      await apiService.updateProfessionalPriorities(businessId, priorityUpdates);
      toast.success('Professional priorities updated successfully!');
      
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (error) {
      console.error('Error updating priorities:', error);
      toast.error('Failed to update priorities');
      // Reload to revert changes
      await loadProfessionals();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Display Order</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-sm text-gray-600 mb-4">
              Drag and drop professionals or use the up/down arrows to reorder their display order. Lower numbers indicate higher priority.
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B4778]"></div>
              </div>
            ) : professionals.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No professionals found who can perform services.
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={professionals.map(p => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {professionals.map((professional, index) => (
                      <SortableProfessionalItem
                        key={professional.id}
                        professional={professional}
                        index={index}
                        totalItems={professionals.length}
                        onMoveUp={() => handleMoveUp(index)}
                        onMoveDown={() => handleMoveDown(index)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
              disabled={saving}
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || professionals.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-[#0B4778] rounded-md hover:bg-[#0a3d66] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalDisplayPriorityModal;
